import { Folder } from '@model/folder';
import { Globals } from '@theme/utils/globals';
import { environment } from '@env/environment';
import { Component, OnInit } from '@angular/core';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { VALIDATION } from '@theme/utils/functions';
import { FolderService } from '@service/folder/folder.service';
import { EmitterService } from '@service/emitter/emitter.service';
import { UploaderService } from '@service/uploader/uploader.service';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FolderAddComponent } from '@client/folder/folder-add/folder-add.component';
import { FolderApportComponent } from '@client/folder/folder-apport/folder-apport.component';
import { WalletTransactionComponent } from '@client/wallet-transaction/wallet-transaction.component';
import { CustomerService } from '@service/customer/customer.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-folder-show',
  templateUrl: './folder-show.component.html',
  styleUrls: ['./folder-show.component.scss']
})
export class FolderShowComponent implements OnInit {
  publicUrl = environment.publicUrl;
  title: string = "";
  folder: Folder;
  global = { country: Globals.country, device: Globals.device };
  userSession = Globals.user;
  file: any;
  validation = VALIDATION;
  validate = false;
  
  // Propriétés pour les apports supplémentaires
  apportsSupplementaires: any[] = [];
  isLoadingApports: boolean = false;
  totalApportsSupplementaires: number = 0;

  // Totaux du tableau échéancier
  totalMensualiteBase: number = 0;
  totalCharge: number = 0;
  totalMontant: number = 0;

  // Pliage/dépliage ancien vs nouveau échéancier
  showAncienEcheancier: boolean = false;
  showNouveauEcheancier: boolean = true;
  echeancesPayees: any[] = [];
  echeancesImpayees: any[] = [];

  // Totaux par section
  totalMensualiteBasePayees: number = 0;
  totalChargePayees: number = 0;
  totalMontantPayees: number = 0;
  totalMensualiteBaseImpayees: number = 0;
  totalChargeImpayees: number = 0;
  totalMontantImpayees: number = 0;

  // Échéancier progressif
  isProgressif: boolean = false;
  totalLoyerProgressif: number = 0;
  isConverting: boolean = false;

  constructor(
    public modale: NgbActiveModal,
    private modalService: NgbModal,
    private emitter: EmitterService,
    private uploader: UploaderService,
    private folderService: FolderService,
    private customerService: CustomerService,
    private toastr: ToastrService
  ) {
    this.validate = this.folderService.validated;
    this.folder = this.folderService.getFolder();
    this.title = "Détails du dossier de " + this.folder?.customer?.nom;
  }

  ngOnInit(): void {
    this.isProgressif = this.folder?.typeEcheancier === 'PROGRESSIF';
    this.chargerApportsSupplementaires();
    this.calculerTotauxEcheancier();
  }

  /**
   * Calculer les totaux du tableau échéancier
   */
  calculerTotauxEcheancier(): void {
    if (!this.folder?.echeances) return;

    // Séparer les échéances payées et impayées
    this.echeancesPayees = this.folder.echeances.filter(e => e.etat === 'PAYE');
    this.echeancesImpayees = this.folder.echeances.filter(e => e.etat !== 'PAYE');

    // Totaux globaux
    this.totalMensualiteBase = this.folder.echeances.reduce((sum, e) => sum + (e.mensualiteBase || 0), 0);
    this.totalCharge = this.folder.echeances.reduce((sum, e) => sum + (e.charge || 0), 0);
    this.totalMontant = this.folder.echeances.reduce((sum, e) => sum + (e.montant || 0), 0);

    // Totaux par section
    this.totalMensualiteBasePayees = this.echeancesPayees.reduce((sum, e) => sum + (e.mensualiteBase || 0), 0);
    this.totalChargePayees = this.echeancesPayees.reduce((sum, e) => sum + (e.charge || 0), 0);
    this.totalMontantPayees = this.echeancesPayees.reduce((sum, e) => sum + (e.montant || 0), 0);

    this.totalMensualiteBaseImpayees = this.echeancesImpayees.reduce((sum, e) => sum + (e.mensualiteBase || 0), 0);
    this.totalChargeImpayees = this.echeancesImpayees.reduce((sum, e) => sum + (e.charge || 0), 0);
    this.totalMontantImpayees = this.echeancesImpayees.reduce((sum, e) => sum + (e.montant || 0), 0);

    // Total progressif
    if (this.isProgressif) {
      this.totalLoyerProgressif = this.folder.echeances.reduce((sum, e) => sum + (e.loyerProgressif || 0), 0);
    }
  }

  /**
   * Charger les apports supplémentaires du dossier
   */
  chargerApportsSupplementaires(): void {
    if (!this.folder?.uuid) return;
    
    this.isLoadingApports = true;
    
    this.folderService.getApportsSupplementaires(this.folder.uuid).subscribe({
      next: (result) => {
        this.apportsSupplementaires = result || [];
        this.calculerTotalApports();
        this.isLoadingApports = false;
      },
      error: (error) => {
        console.error('Erreur chargement apports:', error);
        this.isLoadingApports = false;
        
        // Ne pas afficher d'erreur si c'est juste que le dossier n'a pas d'apports
        if (error.status !== 404) {
          this.toast('Erreur lors du chargement des apports supplémentaires', 'Erreur', 'error');
        }
      }
    });
  }

  /**
   * Calculer le total des apports supplémentaires actifs
   */
  calculerTotalApports(): void {
    this.totalApportsSupplementaires = this.apportsSupplementaires
      .filter(apport => apport.etat === 'VALIDE' && !apport.isAnnule)
      .reduce((total, apport) => total + apport.montantApport, 0);
  }

  /**
   * Ouvrir le modal d'ajout d'apport supplémentaire
   */
  ouvrirModalApport(): void {
    this.folderService.setFolder(this.folder);
    
    const modalRef = this.modalService.open(FolderApportComponent, {
      ariaLabelledBy: 'modal-apport-title',
      size: 'xl',
      centered: true,
      backdrop: 'static'
    });

    modalRef.result.then(
      (result) => {
        if (result) {
          this.rechargerDossier();
        }
      },
      (dismissed) => {
        // Modal fermé sans action
      }
    );
  }

  /**
   * Annuler un apport supplémentaire
   */
  annulerApport(apport: any): void {
    Swal.fire({
      title: 'Confirmer l\'annulation',
      html: `
        <div class="text-left">
          <p><strong>Apport du :</strong> ${new Date(apport.createdAt).toLocaleDateString('fr-FR')}</p>
          <p><strong>Montant :</strong> ${apport.montantApport.toLocaleString()} F CFA</p>
          <p><strong>Type :</strong> ${this.getLibelleChoixRecalcul(apport.choixRecalcul)}</p>
          <hr>
          <p class="text-warning"><i class="fas fa-exclamation-triangle"></i> Cette action est irréversible.</p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirmer l\'annulation',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d'
    }).then((result) => {
      if (result.isConfirmed) {
        this.folderService.annulerApportSupplementaire(apport.uuid).subscribe({
          next: (response) => {
            this.toast('Apport supplémentaire annulé avec succès', 'Annulation réussie', 'success');
            this.rechargerDossier();

            // Émettre un événement pour rafraîchir les autres composants
            this.emitter.emit({
              action: 'APPORT_ANNULE',
              payload: response.data
            });
          },
          error: (error) => {
            const message = error.error?.message || 'Erreur lors de l\'annulation';
            this.toast(message, 'Erreur d\'annulation', 'error');
          }
        });
      }
    });
  }

  /**
   * Obtenir le libellé du choix de recalcul
   */
  getLibelleChoixRecalcul(choixRecalcul: string): string {
    switch (choixRecalcul) {
      case 'REDUIRE_DUREE':
        return 'Réduction de durée';
      case 'REDUIRE_MENSUALITE':
        return 'Réduction de mensualité';
      default:
        return choixRecalcul;
    }
  }

  /**
   * Obtenir la classe CSS pour le badge d'état d'un apport
   */
  getBadgeClassApport(apport: any): string {
    if (apport.isAnnule) {
      return 'badge-danger';
    } else if (apport.etat === 'VALIDE') {
      return 'badge-success';
    } else {
      return 'badge-warning';
    }
  }

  /**
   * Obtenir le texte d'état d'un apport
   */
  getEtatApportText(apport: any): string {
    if (apport.isAnnule) {
      return 'ANNULÉ';
    } else {
      return apport.etat;
    }
  }

  /**
   * Vérifier si le dossier peut recevoir des apports supplémentaires
   */
  peutAjouterApport(): boolean {
    return this.folder?.modalite === 'ECHEANCE' && 
           this.folder?.etat === 'VALIDE' &&
           this.folder?.echeances?.some(e => e.etat === 'IMPAYE');
  }

  /**
   * Vérifie si le dossier peut être converti en échéancier progressif.
   * Conditions :
   *  - Modalité ECHEANCE
   *  - Type actuel = CONSTANT (pas déjà progressif)
   *  - Cas 1 : Dossier INVALIDE (aucune échéance payée par définition)
   *  - Cas 2 : Dossier VALIDE avec 0 échéance payée et 0 paiement
   */
  peutConvertirEnProgressif(): boolean {
    if (!this.folder || this.folder.modalite !== 'ECHEANCE') return false;
    if (this.isProgressif) return false;

    // Cas 1 : Dossier INVALIDE
    if (this.folder.etat === 'INVALIDE') return true;

    // Cas 2 : Dossier VALIDE, aucune échéance payée
    if (this.folder.etat === 'VALIDE') {
      const aucunePayee = !this.folder.echeances?.some(e =>
        e.etat === 'PAYE' || (e.montantPaye && e.montantPaye > 0)
      );
      return aucunePayee;
    }

    return false;
  }

  /**
   * Convertir le dossier en échéancier progressif
   */
  convertirEnProgressif(): void {
    Swal.fire({
      title: 'Convertir en échéancier progressif',
      html: `
        <div class="text-left">
          <p>Cette action va recalculer toutes les échéances avec des <strong>loyers progressifs</strong>.</p>
          <div class="form-group">
            <label>Taux de progression (%)</label>
            <input id="swal-taux" type="number" class="form-control" value="10" min="1" max="50" step="1">
            <small class="text-muted">Ex: 10 = +10% à chaque palier</small>
          </div>
          <div class="form-group mt-2">
            <label>Intervalle (mois)</label>
            <input id="swal-intervalle" type="number" class="form-control" value="60" min="12" step="12">
            <small class="text-muted">Ex: 60 = tous les 5 ans</small>
          </div>
          <hr>
          <p class="text-info"><i class="fas fa-info-circle"></i> Le loyer démarre plus bas et augmente progressivement.</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: '<i class="fas fa-chart-line"></i> Convertir',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#f0ad4e',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
      preConfirm: () => {
        const tauxInput = (document.getElementById('swal-taux') as HTMLInputElement);
        const intervalleInput = (document.getElementById('swal-intervalle') as HTMLInputElement);
        const taux = parseFloat(tauxInput.value) / 100;
        const intervalle = parseInt(intervalleInput.value);

        if (taux <= 0 || taux > 0.5) {
          Swal.showValidationMessage('Le taux doit être entre 1% et 50%');
          return false;
        }
        if (intervalle < 12) {
          Swal.showValidationMessage('L\'intervalle doit être d\'au moins 12 mois');
          return false;
        }
        return { tauxProgression: taux, intervalleProgression: intervalle };
      }
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        this.isConverting = true;
        this.folderService.convertirEnProgressif(
          this.folder.uuid,
          result.value.tauxProgression,
          result.value.intervalleProgression
        ).subscribe({
          next: (res) => {
            if (res?.status === 'success') {
              this.toast('Dossier converti en échéancier progressif avec succès', 'Conversion réussie', 'success');
              this.rechargerDossier();
              this.emitter.emit({ action: 'FOLDER_UPDATED', payload: res?.data });
            }
            this.isConverting = false;
          },
          error: (error) => {
            const msg = error?.error?.message || error?.error?.msg || 'Erreur lors de la conversion';
            this.toast(msg, 'Erreur de conversion', 'error');
            this.isConverting = false;
          }
        });
      }
    });
  }

  /**
   * Vérifie si le dossier COMPTANT validé peut être converti en échéancier annuel.
   */
  peutConvertirEnAnnuel(): boolean {
    if (!this.folder) return false;
    return this.folder.modalite === 'COMPTANT' && this.folder.etat === 'VALIDE';
  }

  /**
   * Convertir le dossier COMPTANT en échéancier annuel
   */
  convertirEnAnnuel(): void {
    Swal.fire({
      title: 'Convertir en échéancier annuel',
      html: `
        <div class="text-left">
          <p>Cette action va convertir le dossier <strong>COMPTANT</strong> en échéancier avec <strong>1 échéance par an</strong>, calculé sur le reste à payer.</p>
          <div class="form-group">
            <label>Nombre d'années</label>
            <input id="swal-nbr-annees" type="number" class="form-control" value="5" min="1" max="30" step="1">
          </div>
          <div class="form-group mt-2">
            <label>Taux d'intérêt annuel (%)</label>
            <input id="swal-taux-annuel" type="number" class="form-control" value="0" min="0" max="100" step="0.5">
            <small class="text-muted">Ex: 5.5 pour 5,5% — 0 pour sans intérêts</small>
          </div>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: '<i class="fas fa-calendar-alt"></i> Convertir',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#f0ad4e',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
      preConfirm: () => {
        const nbrAnnees = parseInt((document.getElementById('swal-nbr-annees') as HTMLInputElement).value, 10);
        const taux = parseFloat((document.getElementById('swal-taux-annuel') as HTMLInputElement).value) / 100;

        if (isNaN(nbrAnnees) || nbrAnnees <= 0) {
          Swal.showValidationMessage('Le nombre d\'années doit être supérieur à 0');
          return false;
        }
        if (isNaN(taux) || taux < 0) {
          Swal.showValidationMessage('Le taux doit être supérieur ou égal à 0');
          return false;
        }
        return { nbrAnnees, taux };
      }
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        this.isConverting = true;
        this.folderService.convertirEnAnnuel(
          this.folder.uuid,
          result.value.nbrAnnees,
          result.value.taux
        ).subscribe({
          next: (res) => {
            if (res?.status === 'success') {
              this.toast('Dossier converti en échéancier annuel avec succès', 'Conversion réussie', 'success');
              this.rechargerDossier();
              this.emitter.emit({ action: 'FOLDER_UPDATED', payload: res?.data });
            }
            this.isConverting = false;
          },
          error: (error) => {
            const msg = error?.error?.message || error?.error?.msg || 'Erreur lors de la conversion';
            this.toast(msg, 'Erreur de conversion', 'error');
            this.isConverting = false;
          }
        });
      }
    });
  }

  /**
   * Recharger le dossier depuis l'API pour avoir les données à jour
   */
  rechargerDossier(): void {
    if (!this.folder?.uuid) return;
    this.folderService.getSingle(this.folder.uuid).subscribe({
      next: (folder: any) => {
        this.folder = folder;
        this.isProgressif = this.folder?.typeEcheancier === 'PROGRESSIF';
        this.folderService.setFolder(this.folder);
        this.calculerTotauxEcheancier();
        this.chargerApportsSupplementaires();
      },
      error: (error) => {
        console.error('Erreur rechargement dossier:', error);
        this.toast('Erreur lors du rechargement du dossier', 'Erreur', 'error');
      }
    });
  }

  /**
   * Imprimer l'échéancier (ANCIEN, VIGUEUR ou COMPLET)
   */
  printerEcheancier(filtre: string): void {
    this.folderService.getPrinterEcheancier(
      this.userSession?.agencyKey,
      this.userSession?.uuid,
      this.folder?.uuid,
      filtre
    );
  }

  editFolder(row) {
    this.modalService.dismissAll();
    this.folderService.setFolder(row);
    this.folderService.edit = true;
    this.modal(FolderAddComponent, 'modal-basic-title', 'xl', true, 'static');
  }

  printerAmortissement(): void {
    this.folderService.getPrinterAmortissement(
      this.userSession?.agencyKey,
      this.userSession?.uuid,
      this.folder?.uuid
    );
  }

  printerFolder(row): void {
    this.folderService.getPrinter('SHOW', this.userSession?.agencyKey, this.userSession?.uuid, row?.uuid);
  }

  activateFolder(row) {
    Swal.fire({
      title: '',
      text: 'Voulez-vous vraiment valider ce dossier ?',
      showCancelButton: true,
      showCloseButton: true,
      cancelButtonText: 'Annuler',
      confirmButtonText: 'Valider <i class="fas fa-check"></i>',
      confirmButtonColor: 'green',
      timer: 2500,
      reverseButtons: true
    }).then((willDelete) => {
      if (willDelete.dismiss) {
      } else {
        this.folderService.activate(row).subscribe((res) => {
          if (res?.status === 'success') {
            this.modale.close('ferme');
            this.emitter.emit({ action: 'FOLDER_ACTIVATE', payload: res?.data });
          }
        });
      }
    });
  }

  modal(component, type, size, center, backdrop) {
    this.modalService.open(component, {
      ariaLabelledBy: type,
      size: size,
      centered: center,
      backdrop: backdrop
    }).result.then((result) => {
    }, (reason) => {
    });
  }

  showFile(item) {
    const fileByFolder = this.uploader.getDataFileByFolder();
    this.file = fileByFolder?.path ? this.publicUrl + '/' + fileByFolder?.path : null;
  }

  closeViewer() {
    this.file = '';
    this.uploader.setDataFileByFolder('');
  }

  /**
   * Afficher un toast
   */
  toast(msg: string, title: string, type: 'info' | 'success' | 'warning' | 'error'): void {
    switch (type) {
      case 'info':
        this.toastr.info(msg, title);
        break;
      case 'success':
        this.toastr.success(msg, title);
        break;
      case 'warning':
        this.toastr.warning(msg, title);
        break;
      case 'error':
        this.toastr.error(msg, title);
        break;
    }
  }

  /**
   * Ouvrir le modal de transaction wallet
   */
  addWalletTransaction(): void {
    if (this.folder?.customer) {
      this.customerService.setCustomer(this.folder.customer);
      const modalRef = this.modalService.open(WalletTransactionComponent, {
        ariaLabelledBy: 'modal-wallet-transaction-title',
        size: 'lg',
        centered: true,
        backdrop: 'static'
      });

      modalRef.result.then(
        (result) => {
          if (result === 'success') {
            // Émettre un événement pour rafraîchir les données si nécessaire
            this.emitter.emit({
              action: 'WALLET_TRANSACTION_COMPLETED',
              payload: this.folder
            });
          }
        },
        (dismissed) => {
          // Modal fermé sans action
        }
      );
    }
  }
}